import { atomWithStorage } from "jotai/utils";

import { Basket, RoamingInfo, SubscriberPackage } from "@/services/model";
import { getPreferredIncludedRoamingPackage } from "@/utils/deviceUtils";
import { env } from "@/utils/environmentHelpers";

export const getHeader = () => ({
  client: Number(env.clientNumericId),
  externalOrderId: "TestOrderID",
  lastUser: 1,
  marketingCampaignId: "TestMarketingCampaignID",
  orderEntryOrganisation: {
    distributionsChannel: 100095212,
    salesPartner: Number(env.clientNumericId),
  },
  salesOrganisation: {
    distributionsChannel: 100095212,
    salesPartner: 1,
  },
  source: 1,
});

export const optionBasketAtom = atomWithStorage<Basket | undefined>(
  "optionBasket",
  undefined,
);

/** A roaming package with a 12-month contract is billed as one yearly package. */
export const isYearlyRoamingPackage = (
  minContractDur?: SubscriberPackage["minContractDur"],
  minContractDurUnit?: SubscriberPackage["minContractDurUnit"],
) => minContractDurUnit === "months" && minContractDur === 12;

export type PartitionedRoamingOptions = {
  /** The included Flat Mobile package shown on the Mobile Abo card, if any. */
  mobileAboPackage?: RoamingInfo;
  /**
   * Booked travel packages plus any surplus `includedInService` packages that
   * did not match the product-matrix `roamingProduct` for the SIM's tariff.
   * Surplus included packages are normalized with `includedInService: false`
   * so travel-package UI treats them as regular booked packages.
   */
  travelPackages: RoamingInfo[];
};

/**
 * Splits roaming options into the Mobile Abo included package and travel
 * packages. When the backend returns more than one `includedInService: true`
 * entry, the one whose `roamingProduct` matches the tariff's product-matrix
 * expectation is preferred; remaining included entries fall back to travel
 * packages. If none match, the first included entry is used as Mobile Abo.
 */
export const partitionRoamingOptionsForDisplay = (
  roamingOptions: RoamingInfo[],
  serviceId?: number,
): PartitionedRoamingOptions => {
  const preferred = getPreferredIncludedRoamingPackage(
    roamingOptions,
    serviceId,
  );
  const notIncluded = roamingOptions.filter(
    (option) => !option.includedInService,
  );

  if (!preferred) {
    return { travelPackages: notIncluded };
  }

  const surplusIncluded = roamingOptions
    .filter((option) => option.includedInService && option !== preferred)
    .map((option) => ({ ...option, includedInService: false }));

  return {
    mobileAboPackage: preferred,
    travelPackages: [...notIncluded, ...surplusIncluded],
  };
};
